import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useSelector } from 'react-redux'

import List from '../../../../../../features/components/List'
import InternalLink from '../../../../../../components/InternalLink'
import ProviderIcon from '../../../../../../features/components/ListItemsTable/Columns/ProviderColumn/ProviderIcon'
import Goal from '../../../../../../features/components/ListItemsTable/Cells/NameCell/Goal'

import ActionsDropdown from '../../../../../../features/components/ActionsDropdown'
import { ReactComponent as DownRightIcon } from '../../../../../../assets/icons/down-right-arrow-icon.svg'

import { redirectTo } from '../../../../../../helpers/url'
import { formatListItemValues } from '../../../../../../features/formatters'
import { useReusableInsightsColumns } from '../../../../../../features/components/ListItemsTable/Columns/useReusableInsightsColumns'
import { createLineItemSummaryPageLink } from '../../../../../CampaignsSummary/helpers'
import { insertIf } from '../../../../../../helpers/common'
import usePermissions from '../../../../../../hooks/usePermissions'

import { listItemsTableColumnsMinWidth } from '../../../../../../features/components/ListItemsTable/columnsWidth'

import { selectedSelfAccountSelector } from '../../../../../../modules/selectors/app'
import { combinedCampaignsSelector } from '../../../../../../modules/selectors/combinedData'

import { ROUTE_PARAMS } from '../../../../../../constants/routes'
import { PLATFORMS_TO_PROVIDER } from '../../../../../../constants/selectLists/platformList'
import { LIST_ITEM_LINE_ITEM } from '../../../../../../constants/other'

import useStyles from './styles'

export const lineItemNameColWidth = 330
const LineItemsBreakdownList = ({ campaign, index, nameColumnLabel = 'Line item', showGoal, handleDeleteRequest }) => {
  const classes = useStyles()

  const { line_items: lineItems = [], campaign_name: campaignName, platform, campaign_id: campaignId } = campaign
  const combinedCampaigns = useSelector(combinedCampaignsSelector)

  const combinedCampaignObjective = combinedCampaigns?.campaigns?.find(c => c.id === campaignId)?.objective

  const campaignProvider = PLATFORMS_TO_PROVIDER[campaign.platform]

  const selfAccountId = useSelector(selectedSelfAccountSelector)

  const permissions = usePermissions()

  const formattedLineItems = useMemo(
    () =>
      lineItems.map(lineItem => ({
        // in come cases we need to have not formatted data e.g. calculate cpa manually,
        // for this case we store original item in data
        data: lineItem,
        ...lineItem,
        ...formatListItemValues(lineItem, { symbol: lineItem['currency_symbol'] })
      })),
    [lineItems]
  )

  const insightsColumns = useReusableInsightsColumns({
    platform,
    items: lineItems,
    listItemType: LIST_ITEM_LINE_ITEM
  })

  const CampaignNameRow = ({ provider }) => {
    return (
      <li className={classnames(classes.campaignName)}>
        <ProviderIcon provider={provider} className={classes.providerIcon} />
        {campaignName}
      </li>
    )
  }

  const goToAdSetHandler = useCallback(
    item => {
      const { ad_account_id: adAccountId, campaign_id: campaignId } = item
      const lineItemSummaryLink = createLineItemSummaryPageLink(item, {
        [ROUTE_PARAMS.selfAccount]: selfAccountId,
        [ROUTE_PARAMS.adAccount]: adAccountId,
        [ROUTE_PARAMS.campaignId]: campaignId
      })

      redirectTo(lineItemSummaryLink)
    },
    [selfAccountId]
  )

  // when change columns/styles, also change Skeleton columns for LineItemsBreakdown
  const itemsListColumns = useMemo(
    () => [
      {
        headTitle: nameColumnLabel,
        Component: item => {
          const { name, ad_account_id: adAccountId, campaign_id: campaignId } = item
          const lineItemSummaryLink = createLineItemSummaryPageLink(item, {
            [ROUTE_PARAMS.selfAccount]: selfAccountId,
            [ROUTE_PARAMS.adAccount]: adAccountId,
            [ROUTE_PARAMS.campaignId]: campaignId
          })

          return (
            <div className={classes.nameCell}>
              <div className={classes.lineItemNameContainer}>
                <InternalLink to={lineItemSummaryLink} className={classes.lineItemNameLink}>
                  <DownRightIcon className={classes.downRightIcon} />
                  <div className={classes.lineItemName}>{name}</div>
                </InternalLink>
              </div>
              {showGoal && <Goal item={item} listItemType={LIST_ITEM_LINE_ITEM} />}
            </div>
          )
        },
        style: {
          minWidth: lineItemNameColWidth
        }
      },
      ...insightsColumns,
      {
        Component: item => {
          return (
            <ActionsDropdown
              options={[
                {
                  text: 'Go to ad set',
                  onClickHandler: () => goToAdSetHandler(item)
                },
                ...insertIf(handleDeleteRequest && permissions.can('delete', 'Ad'), {
                  text: 'Delete',
                  onClickHandler: () => {
                    handleDeleteRequest({
                      item,
                      account: item.ad_account_id,
                      platform: platform,
                      campaignObjective: combinedCampaignObjective
                    })
                  }
                })
              ]}
            />
          )
        },
        style: {
          minWidth: listItemsTableColumnsMinWidth.dropdown,
          maxWidth: listItemsTableColumnsMinWidth.dropdown,
          padding: 0
        }
      }
    ],
    [
      permissions,
      selfAccountId,
      classes,
      insightsColumns,
      goToAdSetHandler,
      nameColumnLabel,
      showGoal,
      handleDeleteRequest,
      combinedCampaignObjective,
      platform
    ]
  )

  if (!lineItems.length) {
    return null
  }

  return (
    <List
      cols={itemsListColumns}
      listData={formattedLineItems}
      SubHeadlineRow={<CampaignNameRow provider={campaignProvider} />}
      hideHeadlineColumns={index !== 0}
    />
  )
}

LineItemsBreakdownList.propTypes = {
  campaign: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  nameColumnLabel: PropTypes.string,
  showGoal: PropTypes.bool,
  handleDeleteRequest: PropTypes.func
}

export default LineItemsBreakdownList
